import React, { useEffect, useRef, useState } from 'react';
import { 
  ArrowRight, 
  ChevronRight,
  Lightbulb,
  Zap,
  Rocket,
  Globe,
  Users,
  Brain,
  Target,
  Cpu,
  Database,
  Wifi,
  Eye,
  Shield,
  Cog,
  TrendingUp,
  Star,
  Play,
  CheckCircle,
  ArrowUp,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Code,
  Layers,
  Atom,
  Microscope,
  Beaker,
  LineChart,
  PieChart,
  BarChart3,
  Settings,
  Factory,
  Wrench,
  Clock
} from 'lucide-react';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

const GaloInnovationLab = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('industrial-robotics');
  
  // Refs for animations
  const heroRef = useRef(null);
  const exploreRef = useRef(null);
  const collaborateRef = useRef(null);
  const projectsRef = useRef(null);
  const partnersRef = useRef(null);
  const processRef = useRef(null);
  const ctaRef = useRef(null);

  // Company details aligned with actual Galo Industries information
  const companyInfo = {
    name: "Galo Industries Innovation Lab",
    parentCompany: "Galo Industries",
    founded: "2016",
    address: "Šibenska ulica 4B, 23000 Zadar, Croatia",
    phone: "+385 98 199 1813",
    email: "info@galo-industries.com",
    website: "https://galo-industries.com",
    hours: "Monday - Friday: 8:00 - 16:00 CET",
    coordinates: { lat: 44.119371, lng: 15.231365 }
  };

  // SEO and Meta data - aligned with actual company
  useEffect(() => {
    // Set page title
    document.title = "Galo Innovation Lab - Industrial Automation R&D | Zadar, Croatia";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Galo Industries Innovation Lab in Zadar, Croatia. Partnering with technology companies to develop advanced industrial automation, robotics, and smart factory solutions for manufacturing excellence.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Galo Industries Innovation Lab in Zadar, Croatia. Partnering with technology companies to develop advanced industrial automation, robotics, and smart factory solutions for manufacturing excellence.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Galo Industries, industrial automation Croatia, robotics development Zadar, smart factory solutions, PLC programming innovation, manufacturing automation R&D, Industry 4.0 Croatia, collaborative robotics, automation engineering Zadar');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Galo Industries, industrial automation Croatia, robotics development Zadar, smart factory solutions, PLC programming innovation, manufacturing automation R&D, Industry 4.0 Croatia, collaborative robotics, automation engineering Zadar';
      document.head.appendChild(meta);
    }

    // Set canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.href);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = window.location.href;
      document.head.appendChild(link);
    }
  }, []);

  // Monitor scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Innovation areas data focused on Galo's core manufacturing business
  const innovationAreas = [
    {
      id: 'industrial-robotics',
      title: 'Industrial Robotics & Automation',
      icon: <Cog className="w-6 h-6" />,
      description: 'Advanced robotic systems including collaborative robots (cobots), industrial manipulators, automated assembly lines, and precision manufacturing robotics for enhanced productivity and safety.',
      projects: ['ABB IRC5 Robot Integration', 'KUKA Cobot Assembly Systems', 'Automated Quality Control Robotics'],
      technologies: ['ABB IRC5 Controllers', 'KUKA Robot Programming', 'Machine Vision Systems', 'Allen-Bradley Safety PLCs'],
      gradient: 'from-blue-500 to-cyan-500',
      seoDescription: 'Industrial robotics solutions including collaborative robots, automated assembly systems, and precision manufacturing robotics.'
    },
    {
      id: 'digital-twins',
      title: 'Digital Twin Technology',
      icon: <Eye className="w-6 h-6" />,
      description: 'Create precise digital replicas of manufacturing processes, equipment, and entire production facilities for real-time monitoring, predictive analytics, and virtual optimization.',
      projects: ['BIW Production Line Digital Twins', 'Equipment Performance Modeling', 'Virtual Commissioning Systems'],
      technologies: ['Siemens Digital Factory', 'Real-time Data Analytics', 'IoT Sensor Integration', 'SCADA Visualization'],
      gradient: 'from-purple-500 to-pink-500',
      seoDescription: 'Digital twin technology for manufacturing optimization, predictive maintenance, and virtual factory simulation.'
    },
    {
      id: 'smart-factory',
      title: 'Smart Factory Solutions',
      icon: <Factory className="w-6 h-6" />,
      description: 'Comprehensive Industry 4.0 implementations featuring connected manufacturing systems, intelligent workflow management, and data-driven production optimization.',
      projects: ['Ford Cologne Smart Factory Implementation', 'Connected Manufacturing Platform', 'Real-time Production Analytics'],
      technologies: ['Siemens S1500 PLCs', 'Industrial Ethernet', 'MES Integration', 'Cloud-based Analytics'],
      gradient: 'from-green-500 to-teal-500',
      seoDescription: 'Smart factory solutions for Industry 4.0 implementation, connected manufacturing, and intelligent production systems.'
    },
    {
      id: 'manufacturing-systems',
      title: 'Advanced Manufacturing Systems',
      icon: <Settings className="w-6 h-6" />,
      description: 'Precision manufacturing technologies including CNC automation, welding systems integration, quality control systems, and advanced material processing.',
      projects: ['Precision Welding Automation', 'CNC Integration Systems', 'Quality Control Automation'],
      technologies: ['Welding Control Systems', 'CNC Programming', 'Laser Control Systems', 'Quality Management'],
      gradient: 'from-orange-500 to-red-500',
      seoDescription: 'Advanced manufacturing systems for precision production, CNC automation, and quality control integration.'
    },
    {
      id: 'process-automation',
      title: 'Process Automation & Control',
      icon: <Cpu className="w-6 h-6" />,
      description: 'Intelligent process control systems, workflow automation, SCADA integration, and PLC programming for optimized manufacturing operations and efficiency.',
      projects: ['Siemens S1500 Automation', 'Allen-Bradley Safety Systems', 'SCADA Modernization'],
      technologies: ['Siemens PLC Programming', 'Allen-Bradley Safety PLCs', 'SCADA Systems', 'HMI Development'],
      gradient: 'from-indigo-500 to-purple-500',
      seoDescription: 'Process automation and control systems for manufacturing efficiency, SCADA integration, and PLC programming.'
    },
    {
      id: 'industrial-iot',
      title: 'Industrial IoT & Connectivity',
      icon: <Wifi className="w-6 h-6" />,
      description: 'Industrial Internet of Things solutions with sensor integration, real-time monitoring, predictive maintenance, and comprehensive equipment connectivity.',
      projects: ['Predictive Maintenance IoT', 'Equipment Connectivity Platform', 'Real-time Monitoring Systems'],
      technologies: ['Industrial Sensors', 'Ethernet/IP Protocol', 'Edge Computing', 'SEW Drive Integration'],
      gradient: 'from-cyan-500 to-blue-500',
      seoDescription: 'Industrial IoT solutions for manufacturing connectivity, sensor integration, and predictive maintenance systems.'
    }
  ];

  // Collaboration models with detailed benefits
  const collaborationModels = [
    {
      title: 'Innovation Partnership Program',
      description: 'Joint research and development projects with shared intellectual property and collaborative technology development',
      icon: <Users className="w-8 h-8" />,
      benefits: ['Access to Galo\'s 9+ years manufacturing expertise', 'Real-world testing in automotive environments', 'European market expansion support', 'Technical mentorship from industry experts']
    },
    {
      title: 'Manufacturing Pilot Program',
      description: 'Test and validate your innovative solutions in real-world manufacturing environments with industry-standard conditions',
      icon: <Microscope className="w-8 h-8" />,
      benefits: ['Validation in automotive production lines', 'Direct feedback from Ford, JLR, VW clients', 'Case study development support', 'Proof-of-concept in live environments']
    },
    {
      title: 'Innovation Sprint Program',
      description: 'Rapid prototyping and proof-of-concept development with intensive mentoring support',
      icon: <Rocket className="w-8 h-8" />,
      benefits: ['Fast validation with industry standards', 'Expert PLC and robotics mentoring', 'Access to automotive industry insights', 'Professional network in Europe']
    },
    {
      title: 'Technology Integration Partnership',
      description: 'Seamlessly integrate your innovative technology into our existing manufacturing solutions and customer base',
      icon: <Layers className="w-8 h-8" />,
      benefits: ['Access to 50+ project portfolio', 'Integration with major OEMs', 'Established automotive customer base', 'Revenue sharing opportunities']
    }
  ];

  // Success metrics aligned with actual company performance
  const metrics = [
    { number: '15+', label: 'Active Innovation Projects', icon: <Beaker className="w-5 h-5" />, description: 'Currently developing solutions for automotive industry' },
    { number: '25+', label: 'Technology Partnerships', icon: <Rocket className="w-5 h-5" />, description: 'Successful collaborations with innovative companies' },
    { number: '50+', label: 'Implemented Projects', icon: <Factory className="w-5 h-5" />, description: 'Successful automation implementations across Europe' },
    { number: '9+', label: 'Years Experience', icon: <TrendingUp className="w-5 h-5" />, description: 'Deep manufacturing automation expertise since 2016' }
  ];

  // Current innovation projects aligned with actual company projects
  const currentProjects = [
    {
      title: 'Next-Gen Automotive Assembly Automation',
      status: 'Active Development',
      description: 'Advanced assembly line automation featuring collaborative robotics, intelligent quality control, and flexible manufacturing cells based on our Ford Cologne and Jaguar Land Rover experience.',
      technologies: ['ABB IRC5', 'Allen-Bradley Safety PLCs', 'Machine Vision', 'Collaborative Robotics'],
      partner: 'European Automotive Supplier',
      category: 'Industrial Robotics',
      realWorldApplication: 'Based on Ford Cologne BIW & Assembly experience'
    },
    {
      title: 'Smart Factory Digital Twin Platform',
      status: 'Pilot Testing Phase',
      description: 'Comprehensive digital twin solution for automotive production lines with real-time monitoring, predictive analytics, and virtual commissioning capabilities.',
      technologies: ['Siemens Digital Factory', 'IoT Sensors', 'Real-time Analytics', 'Virtual Commissioning'],
      partner: 'Industry 4.0 Technology Provider',
      category: 'Digital Twin Technology',
      realWorldApplication: 'Implementing at Amazon AOI1 project'
    },
    {
      title: 'AI-Powered Quality Control System',
      status: 'Production Ready',
      description: 'Intelligent quality control platform with machine learning algorithms, automated defect detection, and seamless integration with existing PLC systems.',
      technologies: ['Machine Learning', 'Computer Vision', 'Siemens S1500 Integration', 'Quality Analytics'],
      partner: 'AI Vision Technology Startup',
      category: 'Smart Factory Solutions',
      realWorldApplication: 'Deployed across multiple automotive clients'
    }
  ];

  // Animation setup with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const refs = [heroRef, exploreRef, collaborateRef, projectsRef, partnersRef, processRef, ctaRef];
    refs.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Schema Markup - Updated with real company info */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Galo Industries Innovation Lab",
          "description": "Industrial automation innovation hub partnering with technology companies to develop manufacturing solutions",
          "url": companyInfo.website,
          "logo": `${companyInfo.website}/img/logo.png`,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Šibenska ulica 4B",
            "addressLocality": "Zadar",
            "addressRegion": "Zadar County", 
            "postalCode": "23000",
            "addressCountry": "HR"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": companyInfo.phone,
            "contactType": "Innovation & Business Development",
            "email": companyInfo.email
          },
          "foundingDate": "2016",
          "parentOrganization": {
            "@type": "Organization",
            "name": "Galo Industries"
          },
          "sameAs": [
            "https://linkedin.com/company/galo-industries"
          ]
        })
      }} />

      <Header />

      {/* Hero Section - Updated with real company messaging */}
      <section ref={heroRef} className="relative pt-20 pb-16 sm:pt-24 sm:pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(14,165,233,0.15),transparent_50%)]"></div>
          
          {/* Floating particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400 rounded-full animate-pulse opacity-60" aria-hidden="true"></div>
          <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-blue-400 rounded-full animate-pulse opacity-80" style={{animationDelay: '1s'}} aria-hidden="true"></div>
          <div className="absolute top-1/2 left-3/4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse opacity-70" style={{animationDelay: '2s'}} aria-hidden="true"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left content */}
            <div className="lg:col-span-7 hero-content">
              <div className="mb-6">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 text-cyan-100 text-sm mb-4">
                  <Factory className="w-4 h-4 mr-2" aria-hidden="true" />
                  Galo Industries Innovation Lab • Zadar, Croatia
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                  Where <span className="relative">
                    <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Innovation</span>
                    <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full" aria-hidden="true"></div>
                  </span>
                  <br />Meets Manufacturing
                </h1>
                
                <p className="text-xl sm:text-2xl text-blue-100 mb-8 leading-relaxed max-w-2xl">
                  Building on 9+ years of automotive and manufacturing expertise, Galo Innovation Lab partners with technology companies to develop cutting-edge industrial automation solutions for global markets.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4 mb-10">
                <a 
                  href="/contact" 
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center"
                  aria-label="Start collaborating with Galo Innovation Lab"
                >
                  Start Collaborating
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </a>
                <a 
                  href="#explore" 
                  className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 inline-flex items-center"
                  aria-label="Explore our innovation areas"
                >
                  Explore Innovation Areas
                  <ChevronRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </a>
              </div>
              
              {/* Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {metrics.map((metric, idx) => (
                  <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="flex items-center mb-2">
                      <div className="text-cyan-400 mr-2" aria-hidden="true">
                        {metric.icon}
                      </div>
                      <div className="text-2xl font-bold text-white">{metric.number}</div>
                    </div>
                    <div className="text-blue-200 text-sm">{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right content - 3D visualization mockup */}
            <div className="lg:col-span-5 hero-visual">
              <div className="relative">
                {/* Main container */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl">
                  {/* Innovation network visualization */}
                  <div className="relative h-80" role="img" aria-label="Innovation ecosystem visualization showing connected technologies">
                    {/* Central hub */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      <Brain className="w-8 h-8 text-white" aria-hidden="true" />
                    </div>
                    
                    {/* Innovation nodes */}
                    <div className="absolute top-4 left-8 w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg flex items-center justify-center text-white shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300">
                      <Cpu className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div className="absolute top-4 right-8 w-12 h-12 bg-gradient-to-r from-green-400 to-teal-500 rounded-lg flex items-center justify-center text-white shadow-lg transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                      <Cog className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg flex items-center justify-center text-white shadow-lg transform rotate-45 hover:rotate-0 transition-transform duration-300">
                      <Eye className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div className="absolute bottom-4 right-4 w-12 h-12 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center text-white shadow-lg transform -rotate-45 hover:rotate-0 transition-transform duration-300">
                      <Shield className="w-6 h-6" aria-hidden="true" />
                    </div>
                    
                    {/* Connection lines */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
                      <defs>
                        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="rgba(34, 211, 238, 0.4)" />
                          <stop offset="100%" stopColor="rgba(59, 130, 246, 0.4)" />
                        </linearGradient>
                      </defs>
                      <line x1="50%" y1="50%" x2="20%" y2="15%" stroke="url(#connectionGradient)" strokeWidth="2" />
                      <line x1="50%" y1="50%" x2="80%" y2="15%" stroke="url(#connectionGradient)" strokeWidth="2" />
                      <line x1="50%" y1="50%" x2="15%" y2="85%" stroke="url(#connectionGradient)" strokeWidth="2" />
                      <line x1="50%" y1="50%" x2="85%" y2="85%" stroke="url(#connectionGradient)" strokeWidth="2" />
                    </svg>
                  </div>
                  
                  {/* Innovation stats */}
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-white mb-1">Innovation Ecosystem</h2>
                      <p className="text-blue-200 text-sm">Connecting Technology & Manufacturing</p>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full opacity-80 animate-bounce" aria-hidden="true"></div>
                <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full opacity-60 animate-bounce" style={{animationDelay: '1s'}} aria-hidden="true"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation Areas Section */}
      <section id="explore" ref={exploreRef} className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16 explore-header">
            <div className="inline-flex items-center mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600" aria-hidden="true"></div>
              <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Innovation Areas</span>
              <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500" aria-hidden="true"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              What We're <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Exploring</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Six core technology domains where we're advancing industrial robotics, manufacturing automation, and smart factory innovation based on our proven automotive industry experience
            </p>
          </header>

          {/* Innovation tabs */}
          <div className="mb-12">
            <nav className="flex flex-wrap justify-center gap-2 mb-8" role="tablist" aria-label="Innovation areas">
              {innovationAreas.map((area) => (
                <button
                  key={area.id}
                  onClick={() => setActiveTab(area.id)}
                  role="tab"
                  aria-selected={activeTab === area.id}
                  aria-controls={`panel-${area.id}`}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    activeTab === area.id
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-700 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {area.title}
                </button>
              ))}
            </nav>

            {/* Active tab content */}
            {innovationAreas.map((area) => (
              <div
                key={area.id}
                id={`panel-${area.id}`}
                role="tabpanel"
                aria-labelledby={`tab-${area.id}`}
                className={`transition-all duration-500 ${
                  activeTab === area.id ? 'opacity-100 transform translate-y-0' : 'opacity-0 absolute transform translate-y-4 pointer-events-none'
                }`}
              >
                {activeTab === area.id && (
                  <article className="bg-gradient-to-br from-slate-50 to-cyan-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div>
                        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${area.gradient} text-white mb-6 shadow-lg`} aria-hidden="true">
                          {area.icon}
                        </div>
                        <h3 className="text-3xl font-bold text-slate-900 mb-4">{area.title}</h3>
                        <p className="text-lg text-slate-700 mb-6">{area.description}</p>
                        
                        <div className="mb-6">
                          <h4 className="font-semibold text-slate-900 mb-3">Core Technologies:</h4>
                          <div className="flex flex-wrap gap-2">
                            {area.technologies.map((tech, idx) => (
                              <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <a 
                          href="/contact"
                          className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${area.gradient} text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
                        >
                          Explore Partnership Opportunities
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </a>
                      </div>
                      
                      <aside>
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                          <h4 className="font-semibold text-slate-900 mb-4">Current Projects:</h4>
                          <ul className="space-y-4">
                            {area.projects.map((project, idx) => (
                              <li key={idx} className="flex items-center p-3 bg-slate-50 rounded-lg">
                                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${area.gradient} mr-3`} aria-hidden="true"></div>
                                <span className="text-slate-700">{project}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </aside>
                    </div>
                  </article>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration Models Section */}
      <section id="collaborate" ref={collaborateRef} className="py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16 collaborate-header">
            <div className="inline-flex items-center mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600" aria-hidden="true"></div>
              <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Collaboration</span>
              <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500" aria-hidden="true"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              How We <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Collaborate</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Multiple partnership models designed to leverage our 50+ project portfolio and automotive industry expertise for maximum innovation impact
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 collaborate-models">
            {collaborationModels.map((model, idx) => (
              <article key={idx} className="bg-white rounded-2xl p-6 shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group">
                <header className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center text-cyan-700 group-hover:from-cyan-500 group-hover:to-blue-600 group-hover:text-white transition-all duration-300" aria-hidden="true">
                    {model.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{model.title}</h3>
                  <p className="text-slate-600 text-sm">{model.description}</p>
                </header>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-2">Key Benefits:</h4>
                    <ul className="space-y-1">
                      {model.benefits.map((benefit, bIdx) => (
                        <li key={bIdx} className="text-xs text-slate-600 flex items-center">
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" aria-hidden="true" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <a 
                  href="/contact"
                  className="w-full mt-6 px-4 py-2 bg-gradient-to-r from-slate-100 to-cyan-50 text-slate-700 rounded-lg font-medium hover:from-cyan-500 hover:to-blue-600 hover:text-white transition-all duration-300 inline-flex items-center justify-center"
                >
                  Learn More
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Current Projects Section */}
      <section id="projects" ref={projectsRef} className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16 projects-header">
            <div className="inline-flex items-center mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600" aria-hidden="true"></div>
              <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Innovation In Action</span>
              <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500" aria-hidden="true"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Current <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Projects</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real innovation projects currently in development with our technology partners, transforming manufacturing through automotive industry-proven solutions
            </p>
          </header>

          <div className="space-y-8 projects-list">
            {currentProjects.map((project, idx) => (
              <article key={idx} className="bg-gradient-to-r from-slate-50 to-cyan-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-all duration-300">
                <div className="grid lg:grid-cols-12 gap-6 items-center">
                  <div className="lg:col-span-8">
                    <header className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{project.title}</h3>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className={`px-3 py-1 rounded-full font-medium ${
                            project.status === 'Active Development' ? 'bg-blue-100 text-blue-700' :
                            project.status === 'Pilot Testing Phase' ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {project.status}
                          </span>
                          <span className="text-slate-600">Partner: {project.partner}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-cyan-700 bg-cyan-50 px-3 py-1 rounded-full mb-2">
                          {project.category}
                        </div>
                        {project.realWorldApplication && (
                          <div className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                            {project.realWorldApplication}
                          </div>
                        )}
                      </div>
                    </header>
                    
                    <p className="text-slate-700 mb-4">{project.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, tIdx) => (
                        <span key={tIdx} className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <aside className="lg:col-span-4">
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-slate-900 mb-4">Project Focus Areas</h4>
                      
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-cyan-600 mb-1">
                            <LineChart className="w-5 h-5 mx-auto" aria-hidden="true" />
                          </div>
                          <div className="text-xs text-slate-600">Analytics</div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-blue-600 mb-1">
                            <Cpu className="w-5 h-5 mx-auto" aria-hidden="true" />
                          </div>
                          <div className="text-xs text-slate-600">AI/ML</div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-purple-600 mb-1">
                            <Wifi className="w-5 h-5 mx-auto" aria-hidden="true" />
                          </div>
                          <div className="text-xs text-slate-600">IoT</div>
                        </div>
                      </div>
                    </div>
                  </aside>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Process Section */}
      <section ref={processRef} className="py-20 lg:py-24 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16 process-header">
            <div className="inline-flex items-center mb-4">
              <div className="h-px w-12 bg-white/40" aria-hidden="true"></div>
              <span className="text-cyan-100 font-semibold uppercase text-sm tracking-wider mx-3">Partnership Process</span>
              <div className="h-px w-12 bg-white/40" aria-hidden="true"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              From Idea to <span className="text-cyan-200">Innovation</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Our proven methodology to transform manufacturing challenges into innovative automation solutions with measurable production impact, backed by 9+ years of automotive industry experience
            </p>
          </header>

          <div className="grid md:grid-cols-4 gap-8 process-steps">
            {[
              {
                step: '01',
                title: 'Initial Contact & Assessment',
                description: `Contact our innovation team at ${companyInfo.email} with your technology proposal and technical documentation`,
                icon: <Mail className="w-8 h-8" />
              },
              {
                step: '02',
                title: 'Technical Evaluation',
                description: 'Comprehensive technical and market fit evaluation with our experienced automation engineers and industry experts',
                icon: <Microscope className="w-8 h-8" />
              },
              {
                step: '03',
                title: 'Pilot Planning & Design',
                description: 'Define project scope, success metrics, and collaboration framework leveraging our automotive project portfolio',
                icon: <Target className="w-8 h-8" />
              },
              {
                step: '04',
                title: 'Implementation & Scale',
                description: 'Execute pilot project with continuous support, monitoring, and guidance toward commercial success in global markets',
                icon: <Rocket className="w-8 h-8" />
              }
            ].map((step, idx) => (
              <article key={idx} className="text-center process-step">
                <div className="relative mb-6">
                  <div className="w-20 h-20 mx-auto bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-lg hover:bg-white/20 transition-all duration-300">
                    <div className="text-cyan-200" aria-hidden="true">
                      {step.icon}
                    </div>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {step.step}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-blue-100 mb-3 text-sm">{step.description}</p>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="/contact"
              className="px-8 py-4 bg-white text-blue-700 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center text-lg"
            >
              Start Your Partnership Journey
              <ArrowRight className="ml-3 w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Contact Information */}
            <div>
              <div className="inline-flex items-center mb-4">
                <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600" aria-hidden="true"></div>
                <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Get In Touch</span>
                <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500" aria-hidden="true"></div>
              </div>
              
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Ready to <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Innovate</span> Together?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Contact Galo Innovation Lab to discuss partnership opportunities and explore how we can transform your technology into manufacturing solutions.
              </p>
              
              {/* Contact Cards */}
              <div className="space-y-4 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Innovation Partnerships</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-cyan-600 hover:text-cyan-700 transition-colors">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Direct Contact</h3>
                      <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-cyan-600 hover:text-cyan-700 transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Visit Our Lab</h3>
                      <p className="text-slate-600">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white mr-4">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Office Hours</h3>
                      <p className="text-slate-600">{companyInfo.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* CTA Section */}
            <div>
              <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-8 lg:p-12 shadow-2xl border border-slate-200 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]" aria-hidden="true"></div>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(14,165,233,0.1),transparent_50%)]" aria-hidden="true"></div>
                
                <div className="relative z-10 text-center">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 text-cyan-200 text-sm mb-6">
                    <Rocket className="w-4 h-4 mr-2" aria-hidden="true" />
                    Partnership Opportunities Available
                  </div>
                  
                  <h3 className="text-3xl font-bold text-white mb-6">
                    Let's Build the Future of <span className="text-cyan-400">Manufacturing</span>
                  </h3>
                  
                  <p className="text-lg text-blue-100 mb-8">
                    Join Galo Innovation Lab and transform your startup's technology into scalable manufacturing solutions that drive global industrial innovation.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="/contact"
                      className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center justify-center"
                    >
                      Submit Partnership Proposal
                      <Mail className="ml-2 w-5 h-5" aria-hidden="true" />
                    </a>
                    <a 
                      href="/services"
                      className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
                    >
                      View Our Services
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Now properly separated */}
      <Footer />

      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-50 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <style jsx>{`
        /* Animation classes */
        .hero-content,
        .hero-visual,
        .explore-header,
        .collaborate-header,
        .collaborate-models,
        .projects-header,
        .projects-list,
        .process-header,
        .process-steps,
        .cta-content {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .animate-in .hero-content,
        .animate-in .explore-header,
        .animate-in .collaborate-header,
        .animate-in .projects-header,
        .animate-in .process-header {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.1s;
        }

        .animate-in .hero-visual,
        .animate-in .collaborate-models,
        .animate-in .projects-list,
        .animate-in .process-steps,
        .animate-in .cta-content {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.3s;
        }

        /* Individual card animations */
        .animate-in .collaborate-models > *,
        .animate-in .projects-list > *,
        .animate-in .process-steps > * {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.6s forwards;
        }

        .animate-in .collaborate-models > *:nth-child(1) { animation-delay: 0.1s; }
        .animate-in .collaborate-models > *:nth-child(2) { animation-delay: 0.2s; }
        .animate-in .collaborate-models > *:nth-child(3) { animation-delay: 0.3s; }
        .animate-in .collaborate-models > *:nth-child(4) { animation-delay: 0.4s; }

        .animate-in .process-steps > *:nth-child(1) { animation-delay: 0.1s; }
        .animate-in .process-steps > *:nth-child(2) { animation-delay: 0.2s; }
        .animate-in .process-steps > *:nth-child(3) { animation-delay: 0.3s; }
        .animate-in .process-steps > *:nth-child(4) { animation-delay: 0.4s; }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Reduce motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
          .hero-content,
          .hero-visual,
          .explore-header,
          .collaborate-header,
          .collaborate-models,
          .projects-header,
          .projects-list,
          .process-header,
          .process-steps,
          .cta-content {
            transition: none;
            opacity: 1;
            transform: translateY(0);
          }

          .animate-in .collaborate-models > *,
          .animate-in .projects-list > *,
          .animate-in .process-steps > * {
            animation: none;
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #0891b2, #1d4ed8);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #0e7490, #1e40af);
        }
      `}</style>
    </div>
  );
};

export default GaloInnovationLab;